import { useRef, useEffect } from 'react';

import { Box } from 'shared';

import { useResizeContext } from '../context';

export const ResizeTrigger = () => {
  const {
    setStartItemSize,
    setIsResize,
    isResize,
    isHovered,
    setIsHovered,
    minStartSize,
    maxStartSize,
    startItemSize,
  } = useResizeContext();

  const dividerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const startPosRef = useRef(0);
  const startSizeRef = useRef(0);

  useEffect(() => {
    const divider = dividerRef.current;
    if (!divider) {
      return;
    }

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      startPosRef.current = e.clientX;
      startSizeRef.current = startItemSize;

      setIsResize(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) {
        return;
      }

      e.preventDefault();
      const newSize = startSizeRef.current + (e.clientX - startPosRef.current);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const clampedSize = Math.max(
          minStartSize,
          Math.min(maxStartSize, newSize)
        );
        setStartItemSize(clampedSize);
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDraggingRef.current) {
        return;
      }

      e.preventDefault();
      isDraggingRef.current = false;
      setIsResize(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      if (!isDraggingRef.current) {
        setIsHovered(false);
      }
    };

    divider.addEventListener('mousedown', handleMouseDown);
    divider.addEventListener('mouseenter', handleMouseEnter);
    divider.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('mousemove', handleMouseMove, { passive: false });
    window.addEventListener('mouseup', handleMouseUp, { passive: false });

    return () => {
      divider.removeEventListener('mousedown', handleMouseDown);
      divider.removeEventListener('mouseenter', handleMouseEnter);
      divider.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [
    setStartItemSize,
    setIsResize,
    setIsHovered,
    minStartSize,
    maxStartSize,
    startItemSize,
  ]);

  return (
    <Box
      position="absolute"
      right="-10px"
      height="100%"
      top={0}
      bottom={0}
      width="20px"
      display="flex"
      justifyContent="center"
      cursor="col-resize"
      bg="transparent"
      ref={dividerRef}
    >
      <Box
        width="4px"
        height="100%"
        bg={isHovered || isResize ? 'var(--fgInteractive)' : 'transparent'}
      />
    </Box>
  );
};
